import {
  useJsonForms,
  withJsonFormsControlProps,
  withJsonFormsMultiEnumProps,
} from "@jsonforms/react";
import { Dropdown } from "pepsico-ds";
import { useEffect, useMemo, useRef } from "react";
import { useStoreGroups } from "../../../hooks/useStoreGroups";

const StoreGroupSelectComponent = ({
  handleChange,
  label,
  path,
  required,
  visible,
  enabled,
  data,
  uischema,
  errors,
}) => {
  const { storeGroups, loading, error } = useStoreGroups();
  const isMultiple = uischema?.options?.multiple !== false;

  const selected = useMemo(() => {
    if (isMultiple) {
      if (!Array.isArray(data)) return [];
      return data.map((id) => {
        return (
          storeGroups.find((option) => option.id === id) || {
            id,
            displayText: id,
          }
        );
      });
    }
    if (!data) return [];
    const match = storeGroups.find((option) => option.id === data);
    return match ? [match] : [{ id: data, displayText: data }];
  }, [storeGroups, data, isMultiple]);

  const dropdownRef = useRef(null);
  useEffect(() => {
    const input = dropdownRef.current?.input;
    if (!isMultiple || !input || selected.length === 0) return;
    const text = selected[0].displayText;
    if (input.value !== text) input.value = text;
  });

  const handleSelectValue = (values) => {
    if (isMultiple) {
      const selectedIds = Array.isArray(values)
        ? values.map((value) => value.id)
        : [];
      handleChange(path, selectedIds);
    } else {
      const value =
        Array.isArray(values) && values.length > 0 ? values[0].id : undefined;
      handleChange(path, value || undefined);
    }
  };

  const ctx = useJsonForms();
  const showValidation = ctx?.config?.showValidation;

  const errMsg =
    errors && errors.includes("must NOT have fewer than 1 characters")
      ? "This field is required"
      : errors || "";

  const hasError = showValidation && !!errMsg;

  if (!visible) return null;

  const isEmpty = !loading && !error && storeGroups.length === 0;

  return (
    <div className="multiple-select-wrap peacock-dropdown-show-label">
      <Dropdown
        ref={dropdownRef}
        childList={storeGroups}
        selection={isMultiple ? "multiple" : "single"}
        isRequired={required}
        label={label}
        disabled={!enabled || loading || !!error || isEmpty}
        selectedValue={selected}
        setSelectedValue={handleSelectValue}
        placeholder={
          loading
            ? "Loading Store Groups..."
            : isEmpty
              ? "No Store Groups available"
              : uischema?.placeholder || "Select a Store Group"
        }
        error={hasError}
      />
      {hasError && (
        <p role="alert" className="store-group-error-text">
          {errMsg}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1">
          Unable to load Store Groups. Please try again.
        </p>
      )}
    </div>
  );
};

export const StoreGroupSelectControl = withJsonFormsMultiEnumProps(
  StoreGroupSelectComponent
);

export const StoreGroupSingleSelectControl = withJsonFormsControlProps(
  StoreGroupSelectComponent
);
